import { useEffect, useState } from "react";
import { useNeural } from "@/store/neuralStore";
import { Activity, AlertTriangle, Crosshair, Cpu, Globe, Radio, Satellite, ShieldAlert, Zap } from "lucide-react";

export function MissionControlMode() {
  const agents = useNeural((s) => s.agents);
  const [pulse, setPulse] = useState(0);
  const [feed, setFeed] = useState<string[]>([
    "12:42:01 SYS · Mission Control online",
    "12:42:02 NET · 1,284 agents linked",
    "12:42:03 GOV · Policy v3.2 enforced",
    "12:42:05 MEM · Vector index synced",
  ]);

  useEffect(() => {
    const i1 = setInterval(() => setPulse((p) => (p + 1) % 100), 80);
    const i2 = setInterval(() => {
      const evts = [
        "AGT · Synth-Ω completed task #4928",
        "TOOL · arxiv.search 312ms ok",
        "RISK · spike detected on Critic-Λ — auto-mitigated",
        "MEM · 14 vectors written",
        "NET · 42.7K TPS sustained",
      ];
      setFeed((f) => [`${new Date().toLocaleTimeString()} ${evts[Math.floor(Math.random() * evts.length)]}`, ...f].slice(0, 80));
    }, 1400);
    return () => {
      clearInterval(i1);
      clearInterval(i2);
    };
  }, []);

  return (
    <div className="grid grid-cols-12 grid-rows-6 gap-2 h-full p-2 bg-background">
      {/* Top main map */}
      <Panel title="Global Mesh" icon={Globe} className="col-span-7 row-span-3">
        <div className="relative h-full w-full bg-grid bg-grid-fade rounded">
          {agents.map((a, i) => (
            <div
              key={a.id}
              className="absolute -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${a.x * 100}%`, top: `${a.y * 100}%` }}
            >
              <div
                className="w-3 h-3 rounded-full"
                style={{
                  background: a.color,
                  boxShadow: `0 0 14px ${a.color}, 0 0 28px ${a.color}`,
                  animation: `pulse 1.${i}s ease-in-out infinite alternate`,
                }}
              />
              <div className="text-[9px] font-mono text-muted-foreground mt-1 whitespace-nowrap">{a.name}</div>
            </div>
          ))}
          {/* sweep */}
          <div
            className="absolute inset-0 origin-center pointer-events-none"
            style={{
              background: "conic-gradient(from 0deg, transparent 0deg, hsl(185 100% 55% / 0.15) 30deg, transparent 60deg)",
              transform: `rotate(${pulse * 3.6}deg)`,
              transition: "transform 80ms linear",
            }}
          />
        </div>
      </Panel>

      {/* Vitals */}
      <Panel title="System Vitals" icon={Activity} className="col-span-5 row-span-3">
        <div className="grid grid-cols-2 gap-2 h-full">
          {[
            { l: "Throughput", v: "42.7K t/s", c: "text-primary" },
            { l: "Latency", v: "42 ms",       c: "text-success" },
            { l: "Cost / hr", v: "$3.18",     c: "text-secondary" },
            { l: "Active",   v: `${agents.filter((a) => a.status === "active").length}/${agents.length}`, c: "text-accent" },
          ].map((s) => (
            <div key={s.l} className="rounded-lg bg-surface-2/60 border border-border p-3 flex flex-col justify-between">
              <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{s.l}</div>
              <div className={`text-2xl font-display font-bold ${s.c}`}>{s.v}</div>
              <div className="flex items-end gap-0.5 h-6 mt-1">
                {Array.from({ length: 16 }).map((_, i) => {
                  const h = ((Math.sin((pulse + i * 5) / 4) + 1) / 2) * 100;
                  return <div key={i} className="flex-1 rounded-sm bg-gradient-to-t from-primary/30 to-secondary/70" style={{ height: `${20 + h * 0.7}%` }} />;
                })}
              </div>
            </div>
          ))}
        </div>
      </Panel>

      {/* Live feed */}
      <Panel title="Telemetry Feed" icon={Radio} className="col-span-5 row-span-3">
        <div className="font-mono text-[10px] space-y-0.5 overflow-y-auto h-full">
          {feed.map((line, i) => (
            <div key={i} className={i === 0 ? "text-primary animate-fade-in" : "text-muted-foreground"}>
              {line}
            </div>
          ))}
        </div>
      </Panel>

      {/* Agents grid */}
      <Panel title="Agent Roster" icon={Cpu} className="col-span-4 row-span-3">
        <div className="grid grid-cols-2 gap-1.5 overflow-y-auto h-full">
          {agents.map((a) => (
            <div key={a.id} className="rounded bg-surface-2/60 border border-border p-1.5 text-[10px] font-mono">
              <div className="flex items-center gap-1">
                <span className={`status-dot ${a.status === "active" ? "text-success" : a.status === "paused" ? "text-warning" : "text-muted-foreground"}`} />
                <span className="truncate">{a.name}</span>
              </div>
              <div className="text-muted-foreground">{a.success}% · {a.calls.toLocaleString()}</div>
            </div>
          ))}
        </div>
      </Panel>

      {/* Threats */}
      <Panel title="Threats" icon={ShieldAlert} className="col-span-3 row-span-3">
        <div className="space-y-1.5">
          {[
            { l: "Prompt-injection attempt", lvl: "low" },
            { l: "Cost spike 18% over budget", lvl: "med" },
            { l: "Unverified tool registered", lvl: "low" },
          ].map((t, i) => (
            <div key={i} className="text-[10px] font-mono flex items-center gap-2 p-2 rounded bg-surface-2/60 border border-border">
              <AlertTriangle className={`w-3 h-3 ${t.lvl === "med" ? "text-warning" : "text-success"}`} />
              <span className="flex-1">{t.l}</span>
              <span className="uppercase text-muted-foreground">{t.lvl}</span>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

function Panel({ title, icon: Icon, children, className = "" }: any) {
  return (
    <div className={`glass rounded-lg p-2 flex flex-col overflow-hidden ${className}`}>
      <div className="flex items-center gap-1.5 mb-1.5 px-1">
        <Icon className="w-3 h-3 text-primary" />
        <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{title}</span>
        <span className="ml-auto text-[9px] font-mono text-success">● LIVE</span>
      </div>
      <div className="flex-1 min-h-0 overflow-hidden">{children}</div>
    </div>
  );
}
